import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, PackageCheck } from "lucide-react";
import { api, fmt } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("pending");
  const [order, setOrder] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setStatus(data.payment_status);
        if (data.payment_status === "paid") {
          const o = await api.get(`/orders/by-session/${sessionId}`);
          setOrder(o.data.order);
          return;
        }
      } catch {}
      if (tries.current++ < 20) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="mx-auto max-w-2xl px-4 py-20 text-center" data-testid="payment-success-page">
      {status !== "paid" ? (
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={36} className="animate-spin text-amber-400" />
          <p className="font-heading text-xl text-white" data-testid="payment-confirming">Confirming your payment with Stripe…</p>
        </div>
      ) : (
        <>
          <CheckCircle2 size={56} className="mx-auto text-emerald-400" />
          <h1 className="mt-5 font-heading text-3xl font-bold text-white" data-testid="order-confirmed-heading">Order confirmed</h1>
          {order && (
            <p className="mt-2 text-slate-400">
              Order <span className="font-mono font-semibold text-amber-400" data-testid="confirmed-order-number">{order.order_number}</span> ·{" "}
              {fmt(order.total || order.subtotal)} · confirmation emailed to{" "}
              <span className="text-slate-200">{order.customer.email}</span>
            </p>
          )}
          <div className="card-surface mt-8 p-6 text-left">
            <div className="flex items-center gap-3">
              <PackageCheck size={20} className="text-amber-400" />
              <p className="font-heading font-semibold text-white">Auto-fulfilment in progress</p>
            </div>
            <p className="mt-2 text-sm text-slate-400">
              Your order was placed directly with the manufacturer and will ship straight to you — no warehouse,
              no middleman. You'll get an email with tracking as soon as it ships.
            </p>
            {order && (
              <div className="mt-4 flex items-center gap-2">
                <span className="text-xs text-slate-500">Status:</span>
                <StatusBadge status={order.fulfillment.status} testid="confirmed-fulfillment-status" />
              </div>
            )}
          </div>
          <div className="mt-6 flex justify-center gap-4">
            {order && (
              <Link to={`/track?order=${order.order_number}&email=${encodeURIComponent(order.customer.email)}`} className="btn-gold" data-testid="track-order-btn">
                Track order
              </Link>
            )}
            <Link to="/shop" className="btn-ghost" data-testid="continue-shopping-btn">Keep shopping</Link>
          </div>
        </>
      )}
    </div>
  );
}
